import { useMutation } from '@tanstack/react-query';
import { FC, MouseEvent } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { entityTeamAssociateType, EntityTeamItemFundAssociations } from '@/api/entity/types';
import { _createFundInvitation } from '@/api/fund/mutations';
import { optimisticCreateFundInvitation } from '@/api/fund/set-query-data';
import ArrowRightIcon from '@/assets/svg/ui-elements/arrow-right.svg';
import DeleteIcon from '@/assets/svg/ui-elements/delete-outline.svg';
import PencilIcon from '@/assets/svg/ui-elements/pencil.svg';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { PagePaths } from '@/routes/page-paths';
import {
  cellHeights,
  getTableMinWidth,
  IconButton,
  Table,
  type TableColumn,
  type TableRow,
  TIME_CELL_MIN_WIDTH,
  TimeCell,
  Tooltip,
  Typography,
} from '@/shared/design-system';
import { DeniedReasonTrigger, Permission, useCan } from '@/shared/modules/permissions';
import { useEntitySlice } from '@/stores/selectors';

import {
  FundActionsRow,
  FundsTree,
  FundsTreeBranch,
  FundsTreeTrunk,
  FundTitleAction,
  FundTitleCell,
  FundTitleText,
} from './user-details.styled';

const TOOLTIP_MIN_LENGTH = 20;

const SUSPENDED_FUND_TOOLTIP =
  'This fund is currently suspended. Please contact Gamma Prime immediately to resolve the issue';

export const JOB_TITLE_BLOCKED_TOOLTIP = 'You do not have permission to edit a fund job title.';
export const FUND_ACTION_BLOCKED_TOOLTIP = 'You do not have permission to manage this fund’s team.';

type FundRow = TableRow & {
  fund: EntityTeamItemFundAssociations;
  email: string;
  isEntityMember: boolean;
  isRejectedUser: boolean;
};

const FundTitle: FC<{ row: FundRow }> = ({ row }) => {
  const canManageFundTeam = useCan(Permission.FundManageTeam);

  const { handleOpen: openTitleEditModal } = useGlobalModal('FUND_JOB_TITLE_EDIT_MODAL', {
    fundId: row.fund.fundId,
    memberId: row.fund?.fundMemberId || '',
    title: row.fund?.title || '',
  });

  const title = row.fund?.title || '';

  return (
    <FundTitleCell>
      <Tooltip title={title} disabled={title.length < TOOLTIP_MIN_LENGTH} side="top">
        <FundTitleText>
          <Typography variant="body2" fontWeight={500} noWrap>
            {title || '-'}
          </Typography>
        </FundTitleText>
      </Tooltip>

      {!!title && (
        <FundTitleAction>
          <Tooltip
            title={canManageFundTeam ? 'Edit job title' : JOB_TITLE_BLOCKED_TOOLTIP}
            side="top"
          >
            <DeniedReasonTrigger
              denied={row.isRejectedUser || !row.fund?.fundMemberId || !canManageFundTeam}
            >
              <IconButton
                size="xs"
                color="tertiary"
                variant="ghost"
                aria-label="Edit fund job title"
                disabled={row.isRejectedUser || !row.fund?.fundMemberId || !canManageFundTeam}
                onClick={(event) => {
                  event.stopPropagation();
                  openTitleEditModal();
                }}
              >
                <PencilIcon />
              </IconButton>
            </DeniedReasonTrigger>
          </Tooltip>
        </FundTitleAction>
      )}
    </FundTitleCell>
  );
};

const FundActions: FC<{ row: FundRow }> = ({ row }) => {
  const { fund, email, isEntityMember, isRejectedUser } = row;
  const navigate = useNavigate();
  const { stepForms } = useEntitySlice();

  const isMember = fund.type === entityTeamAssociateType.Member;
  const isFundInvitation = fund.type === entityTeamAssociateType.FundInvitation;
  const isRemoval = isFundInvitation || isMember;

  const canRunFundAction = useCan(Permission.FundManageTeam);

  const { handleOpen: openRemoveUserModal } = useGlobalModal('FUND_REMOVE_USER_MODAL', {
    fundId: fund.fundId,
    fundName: fund?.fundName || '',
    memberId: fund?.fundMemberId || '',
    invitationId: fund?.fundInvitationId || '',
    email,
  });

  const { mutate: createInvitation } = useMutation({ mutationFn: _createFundInvitation });

  const runFundAction = (event: MouseEvent) => {
    event.stopPropagation();

    if (isRemoval) {
      openRemoveUserModal();

      return;
    }

    createInvitation(
      { id: fund.fundId, body: { email, title: 'Team Member' } },
      {
        onSuccess: (res) => {
          optimisticCreateFundInvitation({
            entityId: stepForms.id,
            email,
            fundId: fund.fundId,
            isEntityMember,
            memberOrInvitationId: res.id,
          });
        },
      },
    );
  };

  const actionLabel = isFundInvitation
    ? 'Cancel fund invitation'
    : isMember
      ? 'Remove from fund'
      : 'Invite to fund';

  const actionTooltip = !canRunFundAction
    ? FUND_ACTION_BLOCKED_TOOLTIP
    : isRejectedUser
      ? SUSPENDED_FUND_TOOLTIP
      : actionLabel;

  return (
    <FundActionsRow>
      <Tooltip title="Open fund" side="top">
        <IconButton
          size="sm"
          color="tertiary"
          variant="outline"
          aria-label="Open fund"
          disabled={!fund?.fundApproved}
          onClick={(event) => {
            event.stopPropagation();
            navigate(generatePath(PagePaths.allFundsId, { identifier: fund.slug ?? '' }));
          }}
        >
          <ArrowRightIcon width={16} height={16} />
        </IconButton>
      </Tooltip>

      <Tooltip title={actionTooltip} side="top">
        <DeniedReasonTrigger denied={!canRunFundAction || isRejectedUser}>
          <IconButton
            size="sm"
            color={isRemoval ? 'destructive' : 'primary'}
            variant="outline"
            aria-label={actionLabel}
            disabled={!canRunFundAction || isRejectedUser}
            onClick={runFundAction}
          >
            {isRemoval ? <DeleteIcon /> : <PlusIcon />}
          </IconButton>
        </DeniedReasonTrigger>
      </Tooltip>
    </FundActionsRow>
  );
};

const CONNECTOR_COLUMN_WIDTH = 48;
const ACTION_COLUMN_WIDTH = 112;
const FUNDS_HEADER_HEIGHT = 40;
const FUND_ROW_HEIGHT = cellHeights.lg;

const branchTops = (fundCount: number) => [
  FUNDS_HEADER_HEIGHT / 2,
  ...Array.from(
    { length: fundCount },
    (_, index) => FUNDS_HEADER_HEIGHT + index * FUND_ROW_HEIGHT + FUND_ROW_HEIGHT / 2,
  ),
];

const FUND_COLUMNS: TableColumn<FundRow>[] = [
  {
    key: 'connector',
    header: '',
    width: CONNECTOR_COLUMN_WIDTH,
    cell: () => null,
  },
  {
    key: 'fund',
    header: 'Fund',
    width: '1fr',
    minWidth: 160,
    cell: (row) => (
      <Tooltip
        title={row.fund.fundName ?? ''}
        disabled={(row.fund.fundName ?? '').length < TOOLTIP_MIN_LENGTH}
        side="top"
      >
        <FundTitleText>
          <Typography variant="body2" fontWeight={500} noWrap>
            {row.fund.fundName || '-'}
          </Typography>
        </FundTitleText>
      </Tooltip>
    ),
  },
  {
    key: 'title',
    header: 'Fund Job Title',
    width: '1fr',
    minWidth: 160,
    cell: (row) => <FundTitle row={row} />,
  },
  {
    key: 'memberSince',
    header: 'Team Member Since',
    width: '1fr',
    minWidth: TIME_CELL_MIN_WIDTH,
    cell: (row) =>
      row.fund.memberSince ? (
        <TimeCell date={row.fund.memberSince} />
      ) : (
        <Typography variant="body2" fontWeight={500}>
          -
        </Typography>
      ),
  },
  {
    key: 'actions',
    header: 'Action',
    align: 'right',
    width: ACTION_COLUMN_WIDTH,
    cell: (row) => <FundActions row={row} />,
  },
];

export type MemberFundsTableProps = {
  funds: EntityTeamItemFundAssociations[];
  email: string;
  isEntityMember: boolean;
  isRejectedUser?: boolean;
};

export const MemberFundsTable: FC<MemberFundsTableProps> = ({
  funds,
  email,
  isEntityMember,
  isRejectedUser = false,
}) => {
  const tops = branchTops(funds.length);

  return (
    <FundsTree>
      <FundsTreeTrunk $height={tops[tops.length - 1]} />
      {tops.map((top) => (
        <FundsTreeBranch key={top} $top={top} />
      ))}

      <Table
        columns={FUND_COLUMNS}
        rows={funds.map((fund) => ({
          key: fund.fundId,
          fund,
          email,
          isEntityMember,
          isRejectedUser,
        }))}
        size="lg"
        variant="plain"
        minTableWidth={getTableMinWidth(FUND_COLUMNS)}
      />
    </FundsTree>
  );
};
